import {
    useContext,
    useEffect,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ICommandFooterProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useKbdTouchOnly
} from "../../../kbd/util";
import type {
    CommandContextType,
    CommandHintType
} from "../../type";
import {
    formatNumber
} from "../../../../helpers/locale/format";
import {
    warnOnce
} from "../../../../utils/development";
import {
    CommandContext
} from "../../context";
import Text from "../../../text";
import Kbd from "../../../kbd";

const CommandFooterContent: FC<ICommandFooterProps & {
    context: CommandContextType;
}> = ({
    context,
    hints,
    style
}) => {
    const {
        customLocalize,
        customTheme,
        resultCount,
        pageStack,
        variant,
        query
    } = context;

    const {
        controlPaddings,
        controlHeights,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isTouchOnly = useKbdTouchOnly();

    const {
        container: containerDynamicStyle,
        hint: hintDynamicStyle
    } = useStyles({
        controlPaddings,
        controlHeights,
        borders,
        variant,
        colors,
        spaces
    });

    if(isTouchOnly || variant === "sheet") {
        return null;
    }

    const currentHints: Array<CommandHintType> = hints ?? [
        {
            title: localize("command-hint-navigate"),
            keys: [
                "↑",
                "↓"
            ]
        },
        {
            title: localize("command-hint-select"),
            keys: [
                "enter"
            ]
        },
        ...(pageStack.length ? [
            {
                title: localize("command-hint-back"),
                keys: [
                    "⌫"
                ]
            }
        ] : []),
        {
            title: localize("command-hint-close"),
            keys: [
                "escape"
            ]
        }
    ];

    const renderHint = (hint: CommandHintType) => {
        return <View
            style={[
                stylesheet.hint,
                hintDynamicStyle
            ]}
            key={hint.title}
        >
            <Kbd
                customLocalize={customLocalize}
                displayBehaviour="always"
                customTheme={customTheme}
                keys={hint.keys}
                separator="none"
            />
            <Text
                customTheme={customTheme}
                variant="bodySmallSize"
                color="low"
            >
                {hint.title}
            </Text>
        </View>;
    };

    const renderCount = () => {
        if(!query) {
            return null;
        }

        return <Text
            customTheme={customTheme}
            variant="bodySmallSize"
            style={stylesheet.end}
            color="low"
        >
            {localize(new Intl.PluralRules(activeLocale).select(resultCount) === "one" ? "command-result-count-one" : "command-result-count-other", [
                formatNumber({
                    locale: activeLocale,
                    value: resultCount
                })
            ])}
        </Text>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-hidden={true}
    >
        {currentHints.map(renderHint)}
        {renderCount()}
    </View>;
};

const CommandFooter: FC<ICommandFooterProps> = (props) => {
    const context = useContext(CommandContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "Command.Footer: must be rendered inside a Command."
        });
    }, [
        context
    ]);

    if(!context) {
        return null;
    }

    return <CommandFooterContent
        {...props}
        context={context}
    />;
};
export default Object.assign(CommandFooter, {
    commandPart: "footer"
});
